import {
  httpAdmin,
  httpClient,
  httpIdentityClient,
  httpEnrollmentClient,
  getDecodedToken,
} from '../common/httpClient';
import {
  TypeObj,
  RegisterObj,
  UpdatePasswordObj,
  UserData,
} from '@/Model/model';
import API_CONST from '@/constants/ApiConst';
import APP_CONST from '@/constants/AppConst';
import APP_UTILITIES from '@/utilities/commonFunctions';
import { writeData, clearAllData } from '@/utilities/idbUtility';
import password from '@/store/modules/password';
import { ICreateUser } from '@/Model/programModel';
import { ForgotGuardianPassword } from '@/Model/user/types';
import { Auth0Token } from '@/Model/sessions/types';
import { getInstance } from '@/auth';

export async function userPosts(userId: number): Promise<Array<UserData>> {
  const response = await httpClient().get(API_CONST.GET_USER_POST, {
    params: { userId },
  });
  return response.data as Array<UserData>;
}

export function getApplicationData(id: any): Promise<any> {
  return httpEnrollmentClient()
    .get(`${API_CONST.APPLICATION_DATA}${id}`)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function getUserDetailsResponseObj(id: number): Promise<any> {
  const { isAuthenticated } = getInstance();
  return httpIdentityClient({ interceptor: isAuthenticated })
    .get(`${API_CONST.USERID}${id}`)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function getUserDetailsResponse(): Promise<any> {
  return httpIdentityClient({ interceptor: true })
    .get(API_CONST.USER_INFORMATION)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function notifyInvitationExpired(Obj: any): Promise<any> {
  return httpClient()
    .post(API_CONST.NOTIFY_INVITAION_EXPIRED, { email: Obj.email })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export async function getLoginResponseObj() {
  const {
    isBxPortal,
    isFamilyPortal,
    userRoles,
  } = (await getDecodedToken()) as Auth0Token;
  const accountDesign: any = APP_UTILITIES.getCookie('accountDesign');

  const cookieRemoved: any = APP_UTILITIES.removeCookies();
  if (cookieRemoved) {

    APP_UTILITIES.setCookie('accountDesign', accountDesign, 1);
  }
  return {
    data: { isFamilyPortal, isBxPortal, userRoles },
    type: '',
    status: 200,
    statusText: '',
  };
}

export function getRegisterResponseObj(registerObj: RegisterObj): Promise<any> {
  return httpIdentityClient({ interceptor: false })
    .put(API_CONST.SET_GUARDIAN_PASSWORD, {
      password: registerObj.password,
      confirmPassword: registerObj.confirmPassword,
      passwordResetToken: registerObj.passwordResetToken,
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function getUsernameResponseObj(tokenString: string): Promise<any> {
  return httpIdentityClient({ interceptor: false })
    .post(API_CONST.VALIDATE_GUARDIAN_TOKEN, {
      token: tokenString,
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function checkEmailExist(payload: ICreateUser): Promise<any> {
  return httpIdentityClient({ interceptor: false })
    .post(API_CONST.CREATE_GUARDIAN, payload)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export async function updateUserDetailsResponseObj(userObj: any): Promise<any> {
  const { userId }: any = await getDecodedToken();
  if (APP_UTILITIES.checkBrowserStatus()) {
    const dataToSend: any = prepareDataToSend(userObj);
    dataToSend.id = parseInt(userId);
    clearAllData('profile').then(() => {
      writeData('profile', dataToSend).then(() => {});
    });
    return updateUserProfile(userObj);
  } else {
    const dataToSend: any = prepareDataToSend(userObj);
    dataToSend.id = userId;
    return navigator.serviceWorker.ready.then((sw) => {
      return writeData('sync-profile', dataToSend)
        .then(() => {
          return sw.sync.register('sync-update-profile');
        })
        .then(() => {
          const response = { status: 'data saved to indexed db' };
          return {
            data: response,
            status: APP_CONST.RESPONSE_200,
            statusText: 'OK',
          };
        })
        .catch((error: any) => {
          return {
            data: error,
            status: APP_CONST.RESPONSE_400,
            statusText: error.statusText,
          };
        });
    });
  }
}

export function getChangePasswordResponseObj(
  registerObj: UpdatePasswordObj
): Promise<any> {
  const oldPassword = encodeURIComponent(password.oldPassword);
  registerObj.password = encodeURIComponent(registerObj.password);
  registerObj.confirmPassword = encodeURIComponent(registerObj.confirmPassword);
  return httpClient()
    .put(API_CONST.CHANGE_PASSWORD, {
      currentPassword: oldPassword,
      password: registerObj.password,
      confirmPassword: registerObj.confirmPassword,
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function resendInvitationMail(obj: any): Promise<any> {
  return httpClient()
    .post(API_CONST.RESEND_INVITE, {
      email: obj.email,
      adminName: obj.adminName,
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function logoutResponseObj(): Promise<any> {
  const { logout } = getInstance();
  localStorage.setItem('slug', APP_UTILITIES.accountSlug);
  APP_UTILITIES.removeCookies;
  return logout();
}

export async function updateUserProfileDetailsResponseObj(
  userObj: any
): Promise<any> {
  const { userId }: any = await getDecodedToken();

  if (APP_UTILITIES.checkBrowserStatus()) {
    const dataToSend: any = prepareDataToSend(userObj);

    dataToSend.id = parseInt(userId);
    clearAllData('profile').then(() => {
      writeData('profile', dataToSend).then(() => {});
    });
    return updateUserProfilePic(userObj);
  } else {
    const dataToSend: any = prepareDataToSend(userObj);
    dataToSend.id = userId;
    return navigator.serviceWorker.ready.then((sw) => {
      return writeData('sync-profile', dataToSend)
        .then(() => {
          return sw.sync.register('sync-update-profile');
        })
        .then(() => {
          const response = { status: 'data saved to indexed db' };
          return {
            data: response,
            status: APP_CONST.RESPONSE_200,
            statusText: 'OK',
          };
        })
        .catch((error: any) => {
          return {
            data: error,
            status: APP_CONST.RESPONSE_400,
            statusText: error.statusText,
          };
        });
    });
  }
}

export function resetLink(payload: ForgotGuardianPassword): Promise<any> {
  return httpIdentityClient({ interceptor: true })
    .post(API_CONST.FORGOT_GUARDIAN_PASSWORD, {
      email: payload.email,
      refSource: payload.refSource,
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

function prepareDataToSend(userObj: any) {
  let dataToSend = {};
  if (userObj && userObj.obj && userObj.updateProfile === 'updateUser') {
    dataToSend = {
      id: userObj.obj && userObj.obj.id,
      firstName: userObj.obj && userObj.obj.firstName,
      lastName: userObj.obj && userObj.obj.lastName,
      email: userObj.obj && userObj.obj.email,
      personalInvitationNote: userObj.obj && userObj.obj.personalInvitationNote,
      phoneNumber: userObj.obj && userObj.obj.phoneNumber,
      userRoles: userObj.obj.userRoles,
      resendBy: userObj.obj && userObj.obj.resendBy,
      resendOn: userObj.obj && userObj.obj.resendOn,
      status: userObj.obj.status ? userObj.obj.status : 1,
      inactiveDate: userObj.obj.inactiveDate ? userObj.obj.inactiveDate : '',
    };
  }
  if (userObj && userObj.obj && userObj.updateProfile === 'updateProfile') {
    dataToSend = {
      firstName: userObj.obj.firstName,
      lastName: userObj.obj.lastName,
      phoneNumber: userObj.obj.phoneNumber,
      email: userObj.obj.email,
    };
  } else if (
    userObj &&
    userObj.profileObj &&
    userObj.updateAvatar === 'updateAvatar'
  ) {
    dataToSend = {
      contentType:
        userObj.profileObj.profilePicture &&
        userObj.profileObj.profilePicture.contentType,
      contentBytes:
        userObj.profileObj.profilePicture &&
        userObj.profileObj.profilePicture.contentBytes,
    };
  } else if (
    userObj &&
    userObj.clearPicObj &&
    userObj.clearPicture === 'clearPicture'
  ) {
    dataToSend = {
      contentType: '',
      contentBytes: '',
    };
  }
  return dataToSend;
}

async function updateUserProfile(userObj: any) {
  const dataToSend = prepareDataToSend(userObj);
  const { userId }: any = await getDecodedToken();

  const urlUpdateUserProfile = `${API_CONST.UPDATE_GUARDIAN}/${userId}`;
  return httpClient()
    .put(urlUpdateUserProfile, dataToSend)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

async function updateUserProfilePic(userObj: any) {
  const dataToSend = prepareDataToSend(userObj);
  const { userId }: any = await getDecodedToken();

  const urlUserProfile = API_CONST.UPDATE_PROFILE + "/" + userId;
  return httpClient()
    .put(urlUserProfile, dataToSend)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export async function getScholarsByGuardian(): Promise<any> {

  const { userId }: any = await getDecodedToken();

  return httpEnrollmentClient()
    .get(`${API_CONST.GET_CHILDREN_BY_GUARDIAN}/${userId}/scholars`)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function getScholarsInfoByGuardian(): Promise<any> {
  return httpEnrollmentClient()
    .get(API_CONST.GET_SCHOLAR_INFO_BY_GUARDIAN)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function getAddedScholarData(payload: any) {
  return httpEnrollmentClient()
    .get(
      `${API_CONST.GET_ADDED_SCHOLAR}?scholarId=${
        payload.id
      }&isSiteDeleted=${true}`
    )
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      const res = error;
      return {
        data: res.data.Message || res.data.value || res.data,
        status: res.status,
        statusText: res.statusText,
      };
    });
}

export function getEnrolledApplications() {
  return httpEnrollmentClient()
    .get(API_CONST.GET_ENROLLED_APPLICATIONS)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      const res = error;
      return {
        data: res.data.Message || res.data.value || res.data,
        status: res.status,
        statusText: res.statusText,
      };
    });
}

export function getProgramsData(payload: any) {
  return httpEnrollmentClient()
    .get(
      API_CONST.PROGRAMS_DETAILS +
        `?ScholarId=${payload.ScholarId}&accountId=${payload.accountId}&programId=${payload.programId}&siteId=${payload.siteId}`
    )
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function getAccountDataBySlug(slug: any) {
  return httpAdmin({ interceptor: false })
    .get(API_CONST.ACCOUNT_SLUG + '?slug=' + slug)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function inviteEmergencyContact(contactGuid: string) {
  return httpEnrollmentClient()
    .post(`${API_CONST.GUARDIANS_INVITE_EMERGENCY_CONTACT}`, {
      EmergencyContactId: contactGuid,
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}
export function inviteAuthorizedPickup(
  contactGuid: string,
  scholarGuid: string
) {
  return httpEnrollmentClient()
    .post(`${API_CONST.GUARDIANS_INVITE_AUTHORIZED_PICKUP}`, {
      AuthorisedPickupId: contactGuid,
      ScholarId: scholarGuid,
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function scrubScholarIds(cartId: number): Promise<any> {
  const payload = { cartId: cartId };
  return httpEnrollmentClient()
    .post(API_CONST.DELETE_SCHOLAR_IDS, payload)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export function getDropDownScholars(userId: number): Promise<any> {
  return httpEnrollmentClient()
    .get(`/guardian/${userId}/availableScholarsForApplication`)
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}

export async function changeEmailResponseObj(changeEmailObj: any): Promise<any> {
  const { userId }: any = await getDecodedToken();
  return httpClient()
    .put( `${API_CONST.UPDATE_GUARDIAN}${userId}/email`, {
      email: changeEmailObj.newEmail,
      password: changeEmailObj.password
    })
    .then((response) => {
      return {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      };
    })
    .catch((error) => {
      return {
        data: error.data,
        status: error.status,
        statusText: error.statusText,
      };
    });
}
